import { PricingModeEnum } from '../../command/abstract-service-calculator';
import { QuoteService } from 'quote-domain-core';
import { QuoteServiceArticlePricingService } from './quote-service-article-pricing-service';
import { QuoteServiceBaseCorridorService } from './quote-service-base-corridor-service';
import { QuoteServiceCorridorImpactService } from './quote-service-corridor-impact-service';
import { QuoteServiceDiscountService } from './quote-service-discount-service';
import { QuoteServiceQuantityService } from './quote-service-quantity-service';
import { singleton } from 'tsyringe';

export enum QuoteServiceMergeStrategy {
    ARTICLE_CODE = 'ARTICLE_CODE',
    GROUPING_CODE = 'GROUPING_CODE',
    BASE_QUANTITY = 'BASE_QUANTITY'
}

export type QuoteServiceCorridorPipelineOptions = {
    mergeStrategy: QuoteServiceMergeStrategy;
    pricingMode: PricingModeEnum;
    applySplitOnAppliedPrice?: boolean;
    applyCustomCarpet?: boolean;
    applyLogisticsPrice?: boolean;
    applyMepPrice?: boolean;
    applyContractVtFactor?: boolean;
    applyFreeMonthFactor?: boolean;
    applyServiceStockSuppOffered?: boolean;
    applyWaterBottleDelivery?: boolean;
};

@singleton()
export class QuoteServiceCorridorPipelineService {
    constructor(
        private readonly quantityService: QuoteServiceQuantityService,
        private readonly baseCorridorService: QuoteServiceBaseCorridorService,
        private readonly corridorImpactService: QuoteServiceCorridorImpactService,
        private readonly articlePricingService: QuoteServiceArticlePricingService,
        private readonly discountService: QuoteServiceDiscountService
    ) {
    }

    public execute(quoteService: QuoteService, referential: any, options: QuoteServiceCorridorPipelineOptions): void {
        this.applyMergeStrategy(quoteService, options.mergeStrategy);
        this.baseCorridorService.applyBaseCorridor(quoteService);

        if (options.applyCustomCarpet) {
            this.corridorImpactService.applyCorridorCustomCarpet(quoteService);
        }
        if (options.applyLogisticsPrice) {
            this.corridorImpactService.applyLogisticPrice(quoteService, referential);
        }
        if (options.applyMepPrice) {
            this.corridorImpactService.applyMepPrice(quoteService, referential);
        }
        if (options.applyContractVtFactor) {
            this.corridorImpactService.applyContractVtFactor(quoteService);
        }
        if (options.applySplitOnAppliedPrice && options.pricingMode === PricingModeEnum.FIXED_CORRIDOR) {
            this.articlePricingService.applySplitOnAppliedPrice(quoteService);
        }

        if (options.pricingMode === PricingModeEnum.FIXED_CORRIDOR) {
            this.articlePricingService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(quoteService);
        } else {
            this.articlePricingService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(quoteService);
        }

        if (options.applyFreeMonthFactor) {
            this.corridorImpactService.applyFreeMonthFactor(quoteService);
        }
        if (options.applyServiceStockSuppOffered) {
            this.corridorImpactService.applyServiceStockSuppOffered(quoteService);
        }
        if (options.applyWaterBottleDelivery) {
            this.corridorImpactService.applyWaterBottleDeliveryPrice(quoteService, referential);
        }

        this.baseCorridorService.setArticleCorridorFromArticleLastCorridorState(quoteService);
        this.discountService.calculateDiscount(quoteService);
    }

    private applyMergeStrategy(quoteService: QuoteService, mergeStrategy: QuoteServiceMergeStrategy): void {
        switch (mergeStrategy) {
            case QuoteServiceMergeStrategy.ARTICLE_CODE:
                this.quantityService.mergeAndUpdateTotalQuantityByArticleCode(quoteService);
                break;
            case QuoteServiceMergeStrategy.GROUPING_CODE:
                this.quantityService.mergeAndUpdateTotalQuantityByGroupingCode(quoteService);
                break;
            default:
                this.quantityService.fillTotalQuantityWithBaseQuantity(quoteService);
                break;
        }
    }
}

